import React from "react";
import type Widget from "@/model/widget";
import WidgetCard from "./widget-card";
import { Button } from "@/components/ui/button";
import { useTaxForm } from "@/hooks/use-taxForm";
import useUserInfo from "@/hooks/use-user-info";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import { Skeleton } from "@/components/ui/skeleton";
import LoadingIndiator from "@/pages/components/loading-indiator";
import { downloadBase64File } from "@/lib/utils";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { useNotificationError } from "@/context/error-provider";

type TaxFormWidgetProps = {
  className?: string;
  widget: Widget;
};

const FAX_FORM_TEXT =
  "Your 1099-HC tax form is available for download here. Keep this document for your records or use it when filing your state taxes.";

const TaxFormWidget: React.FunctionComponent<TaxFormWidgetProps> = ({
  widget,
}) => {
  const { memberId } = useUserInfo();
  const {
    data: taxFormData,
    isFetching: isTaxFormFetching,
    isError,
    error,
  } = useTaxForm(memberId);

  const { addError } = useNotificationError();

  const downloadDisabled =
    isTaxFormFetching ||
    Boolean(isError) ||
    Boolean(error) ||
    !taxFormData ||
    taxFormData?.status?.toLowerCase() !== "success";

  const downloadTaxForm = () => {
    if (downloadDisabled || !taxFormData) return;

    if (isTaxFormFetching || isError || error) {
      addError({
        message: "Failed to setup Tax Form Download.",
        type: ErrorNotificationType.ERROR,
      });
      return;
    }

    const success = downloadBase64File(
      taxFormData?.base64Encoded ?? "",
      "TaxForm.pdf",
    );

    if (!success) {
      addError({
        message: "Tax Form download failed.",
        type: ErrorNotificationType.ERROR,
      });
    } else {
      addError({
        message: "Tax Form has been downloaded.",
        type: ErrorNotificationType.INFO,
      });
    }
  };
  const renderWidget = () => {
    if (isTaxFormFetching) return TaxFormSkeleton;
    if (error?.status === 401) return <NoDataAvailable />;
    if (Boolean(isError) || Boolean(error)) return <SomethingWentWrong />;
    if (downloadDisabled) return <NoDataAvailable />;
    return <p className="mb-1 text-sm text-gray-500">{FAX_FORM_TEXT}</p>;
  };

  const btnText = downloadDisabled ? "Tax Form Not Available" : "Download Form";

  const footerContent = (
    <div className="text-center">
      {isTaxFormFetching ? (
        <LoadingIndiator />
      ) : (
        <Button
          variant="default"
          disabled={downloadDisabled}
          onClick={() => downloadTaxForm()}
        >
          {btnText}
        </Button>
      )}
    </div>
  );

  return (
    <WidgetCard
      title={widget?.widgetDisplayName || "Tax Forms"}
      footerContent={footerContent}
    >
      {renderWidget()}
    </WidgetCard>
  );
};

const TaxFormSkeleton = (
  <div className="space-y-2">
    <Skeleton className="h-4 w-full bg-gray-200" />
    <Skeleton className="h-4 w-full bg-gray-200" />
    <Skeleton className="h-4 w-full bg-gray-200" />
  </div>
);

export default TaxFormWidget;
